import { useEffect, useState } from 'react';
import { ConfirmationModal } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import { useDeleteSegment } from '../hooks/use-segments';
import type { SegmentBlockingCampaignApi } from '../types/segments-api.types';
import type { Segment } from '../types/segments.types';
import { getBlockingCampaignsFromError, getSegmentSessionGuidance } from '../utils/segment-api-errors';

export const SEGMENT_DELETE_BLOCKING_COPY =
  "Remove the segment from those campaigns' audience, or wait until they're sent or cancelled, then delete it again.";

export type SegmentDeleteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  segment: Segment | null;
};

const formatCampaignStatus = (status: string): string => {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const BlockingCampaignsDescription = ({
  campaigns,
}: {
  campaigns: SegmentBlockingCampaignApi[];
}) => {
  const count = campaigns.length;
  const campaignWord = count === 1 ? 'campaign' : 'campaigns';
  const verb = count === 1 ? "hasn't" : "haven't";

  return (
    <div className="flex flex-col gap-2">
      <span>
        This segment is still the audience for {count} {campaignWord} that {verb} finished sending:
      </span>
      <ul className="divide-y divide-neutral-100 border-y border-neutral-100">
        {campaigns.map((campaign) => (
          <li key={campaign.itemId} className="flex items-center justify-between gap-3 py-1.5">
            <span className="text-neutral-900">{campaign.name}</span>
            <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700">
              {formatCampaignStatus(campaign.status)}
            </span>
          </li>
        ))}
      </ul>
      <span className="text-[13px] leading-[18px] text-neutral-500">{SEGMENT_DELETE_BLOCKING_COPY}</span>
    </div>
  );
};

export const SegmentDeleteDialog = ({ open, onOpenChange, segment }: SegmentDeleteDialogProps) => {
  const { toast } = useToast();
  const { mutateAsync: deleteSegment, isPending: isDeleting } = useDeleteSegment();
  const [blockingCampaigns, setBlockingCampaigns] = useState<SegmentBlockingCampaignApi[] | null>(
    null
  );

  useEffect(() => {
    if (!open) setBlockingCampaigns(null);
  }, [open]);

  const segmentName = segment?.name.trim() || 'this segment';
  const isBlocked = Boolean(blockingCampaigns);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isDeleting) return;
    onOpenChange(nextOpen);
  };

  const handleConfirm = async () => {
    if (blockingCampaigns) {
      onOpenChange(false);
      return;
    }
    if (!segment?.itemId) return;

    const deletedName = segment.name.trim() || 'Untitled segment';

    try {
      await deleteSegment(segment.itemId);
      toast({
        variant: 'success',
        title: 'Segment deleted',
        description: `"${deletedName}" was deleted.`,
      });
      onOpenChange(false);
    } catch (error) {
      const blocking = getBlockingCampaignsFromError(error);
      if (blocking) {
        setBlockingCampaigns(blocking);
        return;
      }
      toast({
        variant: 'destructive',
        title: 'Failed to delete segment',
        description:
          getSegmentSessionGuidance(error) ??
          (error instanceof Error ? error.message : 'Please try again.'),
      });
    }
  };

  return (
    <ConfirmationModal
      open={open}
      onOpenChange={handleOpenChange}
      title={
        isBlocked ? `Can't delete "${segmentName}"` : `Delete ${segmentName}?`
      }
      description={
        isBlocked && blockingCampaigns ? (
          <BlockingCampaignsDescription campaigns={blockingCampaigns} />
        ) : (
          'This removes the segment definition. It is never sent to; deleting it does not affect any campaign that has already sent to it.'
        )
      }
      confirmText={isBlocked ? 'Got it' : isDeleting ? 'Deleting…' : 'Delete'}
      cancelText="Cancel"
      confirmClassName={isBlocked ? undefined : 'bg-destructive hover:bg-destructive/90 text-white'}
      hideCancel={isBlocked}
      preventAutoClose
      onConfirm={() => void handleConfirm()}
    />
  );
};
